import React from 'react';
import { Box, Divider, SxProps, Tooltip, Typography } from '@mui/material';
import { getMessageAttachmentDownloadLink, Message } from '@uplift/api';
import { useOnScreenObserver } from '../../hooks';
import { useMarkMessagesAsReadMutation } from '@uplift/queries';
import { Color } from '../types';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { debounce } from 'lodash';
import { AttachmentItem } from './Messages.component';
import { downloadFile } from '@uplift/utils';
import { useAlert } from '../../AlertProvider';


export const MessageBubble: React.FC<{
  message: Message;
  direction: 'receive' | 'send';
  color: Color;
  markAsRead?: boolean;
  downloadableAttachments?: boolean;
}> = ({ message, direction, color, markAsRead = true, downloadableAttachments = true }) => {
  const alert = useAlert();
  const markAsReadMutation = useMarkMessagesAsReadMutation();
  const timezone = useTimezone();

  const handleMarkAsRead = React.useMemo(() => debounce(() => {
    if (markAsRead) {
      markAsReadMutation.mutate({ messageId: message.id });
    }
  }, 1000), [ message.id, markAsRead ]);
  const handleMarkAsReadCancel = () => handleMarkAsRead.cancel();
  const screenObserverRef = useOnScreenObserver(() => {
    if (message.seenAt || direction === 'send') return;
    handleMarkAsRead();
  }, () => {
    handleMarkAsReadCancel();
  });

  // Call the cancel function when the component unmounts
  React.useEffect(() => () => handleMarkAsReadCancel(), []);

  const bubbleWrapperStyles = React.useMemo<SxProps>(() => {
    if (direction === 'send') {
      return {
        justifyContent: 'flex-end',
      };
    } else {
      return {
        justifyContent: 'flex-start',
      };
    }
  }, [ direction ]);

  const bubbleStyles = React.useMemo<SxProps>(() => {
    if (direction === 'send') {
      return {
        bgcolor: `${color}.main`,
        color: 'white',
        borderRadius: '8px 8px 0px 8px',
      };
    } else {
      return {
        bgcolor: `${color}.background`,
        borderRadius: '8px 8px 8px 0px',
      };
    }
  }, [ direction, color ]);

  const messageTime = React.useMemo(() => {
    if (!message?.sentAt) return {};

    const date = CalendarDateService.date(message.sentAt).tz(timezone);
    const formattedDate = date.format(DateFormats['dddd, MMMM D, YYYY']);
    const formattedTime = date.format(DateFormats['hh:mm A']);

    return {
      time: formattedTime,
      date: formattedDate,
      datetime: formattedTime + ', ' + formattedDate,
    };
  }, [ message?.sentAt, timezone ]);

  return (
    <Box
      ref={screenObserverRef}
      data-message-id={message.id}
      data-testid="message"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        py: 0.75,
        px: 3,
        bgcolor: message.seenAt ? 'transparent' : 'grey.50',
        ...bubbleWrapperStyles,
      }}
    >
      {direction === 'send' && (
        <Tooltip title={messageTime.datetime} arrow>
          <Typography sx={{ pr: 1, userSelect: 'none', color: 'grey.400' }} component="span" variant="body2" data-testid="message-time">
            {messageTime.time}
          </Typography>
        </Tooltip>
      )}
      <Box
        sx={{
          p: 1,
          width: 'fit-content',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          gap: 1,
          ...bubbleStyles,
        }}
      >
        {message.message}

        {!!message.attachments?.length && (
          <Box sx={{ bgcolor: 'white', color: 'grey.800', minWidth: '300px', borderRadius: '4px' }} data-testid="message-attachments">
            {message.attachments.map((attachment, index) => (
              <React.Fragment key={attachment.id}>
                {!!index && <Divider />}
                <AttachmentItem
                  key={attachment.id}
                  name={attachment.name}
                  onClick={downloadableAttachments ? async () => {
                    try {
                      const response = await getMessageAttachmentDownloadLink(attachment.id);

                      await downloadFile(response.data.downloadLink, attachment.name);
                    } catch (_err) {
                      alert.error('Unable to download document');
                    }
                  } : undefined}
                />
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
      {direction === 'receive' && (
        <Tooltip title={messageTime.datetime} arrow>
          <Typography sx={{ pl: 1, userSelect: 'none', color: 'grey.400' }} component="span" variant="body2" data-testid="message-time">
            {messageTime.time}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};
